<template>
  <le-bubble theme="dark" trigger="manual" :open="showError" :placement="errorPlacement" :hideOnTargetClick="true" :container="container">
    <template slot="target">
      <div :class="['le-select-dropdown', { 'icon-mode': useIcons }, {'icon-text-item': useIconsAndText}, { 'active': isListOpen }, { 'disabled': !enabled }, { 'has-error': hasError }]" @click="onDropdownClick" @focus.prevent="onDropdownFocus">
        <div v-if="shouldShowPlaceholder" class="placeholder">{{placeholder}}</div>
        <div v-else class="content" v-tooltip="selectedItemTooltip">
          <i v-if="useIcons || useIconsAndText" class="icon-item" :class="iconClass"></i>
          <span v-if="useIconsAndText || !useIcons" class="label" ref="label">{{selectedItemValue}}</span>
        </div>
        <i :class="['ico-9', 'ico-arrow', arrowDirectionClass]"></i>
      </div>
    </template>
    <template slot="content">
      {{ bubbleErrorMsg }}
    </template>
  </le-bubble>
</template>

<script>
  import leBubble from '../le-bubble/le-bubble';
  import CONSTS from '../../../consts/consts';

  export default {
    name: 'le-select-dropdown',
    components: {
      leBubble,
    },
    props: {
      selectedItem: {
        type: Object,
      },
      itemValueAttr: {
        type: String,
        default: 'value',
      },
      itemIconClass: {
        type: String,
        default: '',
      },
      isListOpen: {
        type: Boolean,
        required: true,
      },
      useIcons: {
        type: Boolean,
      },
      useIconsAndText: {
        type: Boolean,
      },
      placeholder: {
        type: String,
      },
      enabled: {
        type: Boolean,
        default: true,
      },
      errorPlacement: {
        type: String,
        default: 'bottom',
      },
      container: {
        type: String,
      },
      tooltipPlacement: {
        type: String,
        default: CONSTS.LE_BUBBLE.PLACEMENTS.RIGHT,
      },
    },
    data() {
      return {
        errorMsg: '',
        bubbleErrorMsg: '',
        showError: false,
        tootipText: '',
      };
    },
    computed: {
      selectedItemValue() {
        return this.selectedItem ? this.selectedItem[this.itemValueAttr] : '';
      },
      arrowDirectionClass() {
        return this.isListOpen ? 'ico-arrow-up' : 'ico-arrow-down';
      },
      iconClass() {
        let iconClass = '';
        if (this.useIcons) {
          iconClass = this.selectedItemValue;
        } else if (this.useIconsAndText) {
          iconClass = this.itemIconClass;
        }
        return iconClass;
      },
      shouldShowPlaceholder() {
        return !this.selectedItemValue && this.placeholder;
      },
      hasError() {
        return this.errorMsg.length > 0;
      },
      selectedItemTooltip() {
        return { content: this.tootipText, placement: this.tooltipPlacement, html: false, autoHide: false };
      },
    },
    mounted() {
      this.updateTooltipText();
    },
    updated() {
      this.updateTooltipText();
    },
    methods: {
      updateTooltipText() {
        const hasEllipsis = this.$refs.label && this.$refs.label.scrollWidth > this.$refs.label.offsetWidth;
        this.tootipText = hasEllipsis ? this.selectedItemValue : '';
      },
      onDropdownClick() {
        this.toggleDropdown(!this.isListOpen);
      },
      onDropdownBlur() {
        this.toggleDropdown(false);
      },
      onDropdownFocus() {
        if (this.enabled) {
          this.showError = false;
        }
      },
      toggleDropdown(newState) {
        if (this.enabled) {
          this.showError = false;
          this.$emit('toggle', newState);
        }
      },
      setError(showError, errorMsg) {
        if (showError) {
          this.errorMsg = errorMsg;
          this.bubbleErrorMsg = this.errorMsg;
        } else {
          this.clearError();
        }
        this.toggleErrorBubble(this.hasError);
      },
      clearError() {
        this.toggleErrorBubble(false);
        this.errorMsg = '';
      },
      toggleErrorBubble(showBubble) {
        this.showError = showBubble;
      },
    },
  };
</script>

<style lang="scss">
  $dropdown-height: 32px;
  .le-select-dropdown {
    @include themify {
      border: 1px solid getColor('dropdownButtonBorder');
      background-color: getColor('dropdownButtonBackground');
    }
    border-radius: 1px;
    cursor: pointer;
    outline: none;
    width: 100%;
    box-sizing: border-box;
    height: #{$dropdown-height};
    &.active {
      @include themify {
        border-color: getColor('dropdownButtonBorderActive');
      }
    }

    &.icon-mode {
      text-align: center;
      border: none;
      padding: 0px;
      background: none;
      @include themify {
        color: getColor('text');
      }
      .ico-arrow {
        padding-left: 5px;
        right: auto;
        @include themify {
          color: getColor('text');
        }
      }
    }

    &.icon-text-item {
      .label {
        padding-left: 5px;
        max-width: calc(100% - 61px);
      }
      .icon-item {
        margin-left: 10px;
        display: inline-block;
        vertical-align: top;
      }
    }

    &.has-error {
      @include themify {
        border-color: getColor('error');
      }
    }

    .placeholder {
      padding-left: 9px;
      font-size: 12px;
      line-height: #{$dropdown-height};
      max-width: calc(100% - 30px);
      vertical-align: middle;
      display: inline-block;
      @include ellipsis();
      @include themify {
        color: rgba(getColor('text'), 0.7);
      }
    }

    .label {
      padding-left: 9px;
      font-size: 12px;
      line-height: #{$dropdown-height};
      max-width: calc(100% - 30px);
      vertical-align: middle;
      display: inline-block;
      @include ellipsis();
      @include themify {
        color: getColor('text');
      }
    }

    .icon-item {
      line-height: #{$dropdown-height};
    }

    .ico-arrow {
      position: absolute;
      right: 10px;
      top: 2px;
      vertical-align: middle;
      padding-top: 1px;
      line-height: #{$dropdown-height};
      @include themify {
        color: getColor('dropdownArrow');
      }
    }

    &.disabled {
      cursor: default;
      @include themify {
        border-color: getColor('dropdownBorderDisabled');
        background-color: getColor('dropdownBackgroundDisabled');
      }
      .label {
        @include themify {
          color: getColor('dropdownTextDisabled');
        }
      }
      .ico-arrow {
        @include themify {
          color: getColor('dropdownArrowDisabled');
        }
      }
      .icon-item {
        opacity: .3;
      }
      .placeholder {
        @include themify {
          color: getColor('dropdownTextDisabledPlaceholder');
        }
      }
    }
  }
</style>
